import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { trackLeadConversion } from "@/components/TrackingScripts";
import { injectLandingTracking, cleanupLandingTracking, trackLandingConversion } from "@/lib/landingTracking";
import {
  ArrowRight, Check, CheckCircle2, Download, Clock,
  Shield, Zap, Award, Star, Phone, Mail, Package, Loader2,
  ChevronRight, Calculator, Sun,
} from "lucide-react";
import { SavingsCalculator } from "@/components/SavingsCalculator";
import { CheckboxLabel } from "@/components/CheckboxLabel";
import { CalculatorLeadForm } from "@/components/CalculatorLeadForm";

const API = "/api";

const themes = {
  energy: {
    heroBg: "bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900",
    accent: "text-emerald-400", accentBg: "bg-emerald-500", accentLight: "bg-emerald-50 text-emerald-700",
    ctaBg: "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30",
    priceBg: "bg-emerald-500", border: "border-emerald-200", ring: "ring-emerald-500/20",
    featureIcon: "bg-emerald-100 text-emerald-600", specBg: "bg-emerald-50",
    heroText: "text-white", heroSub: "text-white/70", heroBrand: "text-white/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  premium: {
    heroBg: "bg-gradient-to-br from-slate-950 via-slate-900 to-zinc-900",
    accent: "text-amber-400", accentBg: "bg-amber-500", accentLight: "bg-amber-50 text-amber-700",
    ctaBg: "bg-amber-500 hover:bg-amber-600 shadow-amber-500/30",
    priceBg: "bg-amber-500", border: "border-amber-200", ring: "ring-amber-500/20",
    featureIcon: "bg-amber-100 text-amber-600", specBg: "bg-amber-50/50",
    heroText: "text-white", heroSub: "text-white/70", heroBrand: "text-white/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  flash: {
    heroBg: "bg-gradient-to-br from-red-900 via-red-800 to-orange-900",
    accent: "text-red-400", accentBg: "bg-red-500", accentLight: "bg-red-50 text-red-700",
    ctaBg: "bg-red-500 hover:bg-red-600 shadow-red-500/30",
    priceBg: "bg-red-500", border: "border-red-200", ring: "ring-red-500/20",
    featureIcon: "bg-red-100 text-red-600", specBg: "bg-red-50/50",
    heroText: "text-white", heroSub: "text-white/70", heroBrand: "text-white/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  tech: {
    heroBg: "bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-900",
    accent: "text-blue-400", accentBg: "bg-blue-500", accentLight: "bg-blue-50 text-blue-700",
    ctaBg: "bg-blue-500 hover:bg-blue-600 shadow-blue-500/30",
    priceBg: "bg-blue-500", border: "border-blue-200", ring: "ring-blue-500/20",
    featureIcon: "bg-blue-100 text-blue-600", specBg: "bg-blue-50/50",
    heroText: "text-white", heroSub: "text-white/70", heroBrand: "text-white/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  minimal: {
    heroBg: "bg-white",
    accent: "text-slate-900", accentBg: "bg-slate-900", accentLight: "bg-slate-100 text-slate-700",
    ctaBg: "bg-slate-900 hover:bg-slate-800 shadow-slate-900/20",
    priceBg: "bg-slate-900", border: "border-slate-200", ring: "ring-slate-900/10",
    featureIcon: "bg-slate-100 text-slate-700", specBg: "bg-slate-50",
    heroText: "text-slate-900", heroSub: "text-slate-500", heroBrand: "text-slate-400",
    priceNote: "text-slate-400", heroOverlay: false, darkHero: false,
  },
  solar: {
    heroBg: "bg-gradient-to-br from-amber-800 via-orange-700 to-yellow-600",
    accent: "text-yellow-300", accentBg: "bg-orange-500", accentLight: "bg-orange-50 text-orange-700",
    ctaBg: "bg-orange-500 hover:bg-orange-600 shadow-orange-500/30",
    priceBg: "bg-orange-500", border: "border-orange-200", ring: "ring-orange-500/20",
    featureIcon: "bg-orange-100 text-orange-600", specBg: "bg-orange-50/40",
    heroText: "text-white", heroSub: "text-white/80", heroBrand: "text-yellow-200/60",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  nature: {
    heroBg: "bg-gradient-to-br from-green-950 via-green-900 to-lime-900",
    accent: "text-lime-400", accentBg: "bg-green-600", accentLight: "bg-green-50 text-green-800",
    ctaBg: "bg-green-600 hover:bg-green-700 shadow-green-600/30",
    priceBg: "bg-green-600", border: "border-green-200", ring: "ring-green-600/20",
    featureIcon: "bg-green-100 text-green-700", specBg: "bg-green-50/40",
    heroText: "text-white", heroSub: "text-green-100/70", heroBrand: "text-lime-300/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  ocean: {
    heroBg: "bg-gradient-to-br from-cyan-900 via-teal-800 to-sky-900",
    accent: "text-cyan-300", accentBg: "bg-teal-500", accentLight: "bg-teal-50 text-teal-700",
    ctaBg: "bg-teal-500 hover:bg-teal-600 shadow-teal-500/30",
    priceBg: "bg-teal-500", border: "border-teal-200", ring: "ring-teal-500/20",
    featureIcon: "bg-teal-100 text-teal-600", specBg: "bg-teal-50/40",
    heroText: "text-white", heroSub: "text-white/70", heroBrand: "text-cyan-200/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  sunset: {
    heroBg: "bg-gradient-to-br from-rose-900 via-pink-800 to-amber-700",
    accent: "text-rose-300", accentBg: "bg-rose-500", accentLight: "bg-rose-50 text-rose-700",
    ctaBg: "bg-rose-500 hover:bg-rose-600 shadow-rose-500/30",
    priceBg: "bg-rose-500", border: "border-rose-200", ring: "ring-rose-500/20",
    featureIcon: "bg-rose-100 text-rose-600", specBg: "bg-rose-50/40",
    heroText: "text-white", heroSub: "text-white/80", heroBrand: "text-rose-200/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
  futuristic: {
    heroBg: "bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950",
    accent: "text-cyan-400", accentBg: "bg-cyan-500", accentLight: "bg-cyan-50 text-cyan-700",
    ctaBg: "bg-cyan-500 hover:bg-cyan-400 shadow-cyan-500/30",
    priceBg: "bg-cyan-500", border: "border-cyan-200", ring: "ring-cyan-500/20",
    featureIcon: "bg-cyan-100 text-cyan-600", specBg: "bg-gray-50",
    heroText: "text-white", heroSub: "text-cyan-100/60", heroBrand: "text-cyan-400/50",
    priceNote: "text-white/50", heroOverlay: true, darkHero: true,
  },
};

function DailyTimer({ theme }) {
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      const diff = end - now;
      setTime({ h: Math.floor(diff / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const isDark = theme.darkHero;
  return (
    <div className="flex items-center gap-3 justify-center" data-testid="landing-timer">
      <Clock className={`w-4 h-4 ${theme.accent}`} />
      <span className={`text-xs font-semibold ${isDark ? "text-white/70" : "text-slate-500"}`}>Offerta scade tra:</span>
      <div className="flex gap-1.5">
        {[["h", time.h], ["m", time.m], ["s", time.s]].map(([l, v]) => (
          <div key={l} className="text-center">
            <div className={`${theme.priceBg} text-white font-bold text-lg px-2.5 py-1 rounded-lg min-w-[40px] leading-tight`} style={{ fontFamily: "Outfit, sans-serif" }}>{pad(v)}</div>
            <div className={`text-[9px] ${isDark ? "text-white/50" : "text-slate-400"} mt-0.5 uppercase`}>{l === "h" ? "ore" : l === "m" ? "min" : "sec"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadForm({ landing, theme, otpRequired }) {
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", message: "", privacy_accepted: false });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [privacyText, setPrivacyText] = useState("");
  const [privacyLink, setPrivacyLink] = useState({ text: "", url: "" });
  const [extraCheckboxes, setExtraCheckboxes] = useState([]);
  const [extraChecked, setExtraChecked] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/privacy-text`).then((r) => {
      setPrivacyText(r.data.text || "");
      setPrivacyLink({ text: r.data.link_text || "", url: r.data.link_url || "" });
      setExtraCheckboxes(r.data.checkboxes || []);
    }).catch(() => {});
  }, []);

  const formatItalianPhone = (value) => {
    const digits = value.replace(/[^\d+]/g, "");
    if (digits.startsWith("+") && !digits.startsWith("+39")) {
      return digits;
    }
    return digits;
  };

  const validateItalianPhone = (phone) => {
    const cleaned = phone.replace(/[\s\-().]/g, "");
    // Accept: +39 3xx..., 3xx..., +39 0xx..., 0xx...
    const re = /^(\+39)?(3\d{8,9}|0\d{5,10})$/;
    return re.test(cleaned);
  };

  const handlePhoneChange = (e) => {
    const val = formatItalianPhone(e.target.value);
    set("phone", val);
    if (val && !validateItalianPhone(val)) {
      setPhoneError("Inserisci un numero di telefono italiano valido");
    } else {
      setPhoneError("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.email || !form.phone) { toast.error("Nome, cognome, email e telefono sono obbligatori"); return; }
    if (!validateItalianPhone(form.phone)) { toast.error("Inserisci un numero di telefono italiano valido"); return; }
    if (!form.privacy_accepted) { toast.error("Devi accettare la privacy policy per procedere"); return; }
    const missingRequired = extraCheckboxes.some((cb, i) => cb.required && !extraChecked[i]);
    if (missingRequired) { toast.error("Devi accettare tutti i consensi obbligatori"); return; }
    if (otpRequired && !phoneVerified) { toast.error("Verifica il numero di telefono prima di inviare"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/landings/lead`, { ...form, landing_slug: landing.slug, product_name: landing.product_name });
      trackLeadConversion({ source: "landing_promo", name: form.name, email: form.email, landing_slug: landing.slug, product_name: landing.product_name });
      trackLandingConversion(landing.tracking_codes, { landing_slug: landing.slug, product_name: landing.product_name });
      setSent(true);
      toast.success("Richiesta inviata con successo!");
    } catch { toast.error("Errore nell'invio"); }
    finally { setLoading(false); }
  };

  const sendOtp = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/otp/send`, { phone: form.phone });
      setOtpSent(true);
      toast.success("Codice OTP inviato!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio OTP"); }
    finally { setVerifying(false); }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/otp/verify`, { phone: form.phone, code: otpCode });
      if (res.data.success) { setPhoneVerified(true); toast.success("Numero verificato!"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Codice non valido o scaduto"); }
    finally { setVerifying(false); }
  };

  if (sent) return (
    <div className="text-center py-8 animate-fade-in" data-testid="landing-form-success">
      <div className={`w-16 h-16 rounded-full ${theme.featureIcon} flex items-center justify-center mx-auto mb-4`}>
        <Check className="w-8 h-8" strokeWidth={2.5} />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Richiesta Inviata!</h3>
      <p className="text-sm text-slate-500">Ti contatteremo al piu presto con un preventivo personalizzato.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="landing-lead-form">
      <div className="grid grid-cols-2 gap-3">
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nome *" className="h-11 rounded-xl" data-testid="landing-form-name" />
        <Input value={form.surname} onChange={(e) => set("surname", e.target.value)} placeholder="Cognome *" className="h-11 rounded-xl" data-testid="landing-form-surname" />
      </div>
      <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="La tua email *" className="h-11 rounded-xl" data-testid="landing-form-email" />
      <div>
        <Input type="tel" value={form.phone} onChange={handlePhoneChange} placeholder="Telefono (+39...) *" className={`h-11 rounded-xl ${phoneError ? "border-red-400 focus-visible:ring-red-400" : ""}`} data-testid="landing-form-phone" disabled={phoneVerified} />
        {phoneError && <p className="text-[11px] text-red-500 mt-1" data-testid="phone-error">{phoneError}</p>}
      </div>
      {otpRequired && (
        <div className="bg-slate-50 rounded-xl p-3 space-y-2">
          {!phoneVerified ? (
            !otpSent ? (
              <button type="button" onClick={sendOtp} disabled={verifying || !form.phone || !!phoneError}
                className="w-full bg-slate-900 text-white font-semibold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="landing-otp-send">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} Invia Codice OTP
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-600 text-center font-medium">Inserisci il codice a 6 cifre</p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button type="button" onClick={verifyOtp} disabled={verifying || otpCode.length < 6}
                  className="w-full bg-emerald-500 text-white font-semibold rounded-xl py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="landing-otp-verify">
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verifica Codice
                </button>
              </div>
            )
          ) : (
            <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 justify-center">
              <CheckCircle2 className="w-4 h-4" /> Numero verificato
            </p>
          )}
        </div>
      )}
      <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Messaggio (opzionale)" rows={2}
        className="w-full rounded-xl border border-input px-3 py-2 text-sm resize-none" data-testid="landing-form-message" />
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={form.privacy_accepted} onChange={(e) => set("privacy_accepted", e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0" data-testid="landing-form-privacy" />
        <CheckboxLabel label={privacyText || "Acconsento al trattamento dei miei dati personali."} linkText={privacyLink.text} linkUrl={privacyLink.url} required />
      </label>
      {extraCheckboxes.map((cb, idx) => (
        <label key={idx} className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={!!extraChecked[idx]} onChange={(e) => setExtraChecked(prev => ({ ...prev, [idx]: e.target.checked }))}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0" data-testid={`landing-form-extra-checkbox-${idx}`} />
          <CheckboxLabel label={cb.label} linkText={cb.link_text} linkUrl={cb.link_url} required={cb.required} />
        </label>
      ))}
      <button type="submit" disabled={loading || !!phoneError || !form.privacy_accepted || extraCheckboxes.some((cb, i) => cb.required && !extraChecked[i]) || (otpRequired && !phoneVerified)}
        className={`w-full ${theme.ctaBg} text-white font-bold rounded-xl py-3.5 text-sm shadow-xl flex items-center justify-center gap-2 disabled:opacity-50`}
        data-testid="landing-form-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {landing.cta_text || "Richiedi Preventivo"}
      </button>
    </form>
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/landings/${slug}`)
      .then((r) => {
        setLanding(r.data);
        // Inject landing-specific tracking codes
        if (r.data.tracking_codes?.length > 0) {
          injectLandingTracking(r.data.tracking_codes);
        }
        // Track page view
        axios.post(`${API}/landings/track`, {
          landing_slug: slug,
          referrer: document.referrer || "",
          user_agent: navigator.userAgent || "",
        }).catch(() => {});
      })
      .catch(() => setLanding(null))
      .finally(() => setLoading(false));
    return () => cleanupLandingTracking();
  }, [slug]);

  useSEO({
    title: landing ? `${landing.title} | Offerta Speciale` : "Promo",
    description: landing?.subtitle || landing?.product_description || "",
    ogImage: landing?.hero_image_url || "",
    canonical: `${window.location.origin}/promo/${slug}`,
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!landing || landing.status !== "active") return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" data-testid="landing-not-found">
      <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Promozione non disponibile</h1>
      <button onClick={() => navigate("/")} className="bg-emerald-500 text-white font-bold rounded-full px-6 py-2.5 text-sm">Torna alla Home</button>
    </div>
  );

  const t = themes[landing.template_style] || themes.energy;
  const specLines = landing.product_specs ? landing.product_specs.split("\n").filter((l) => l.trim()) : [];
  const features = landing.features?.filter((f) => f.title) || [];
  const featureIcons = [Zap, Shield, Award, Star, CheckCircle2, Package];

  const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Logo Header */}
      <header className={`relative z-10 ${t.heroBg}`} data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-0">
          <a href="/" className="inline-flex items-center gap-2.5 group">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-20 sm:h-24 w-auto object-contain" data-testid="landing-logo" />
            <span className={`text-sm font-bold tracking-tight ${t.heroText} opacity-80 hidden sm:inline`} style={{ fontFamily: "Outfit, sans-serif" }}>
              Fotovoltaico Chiavi in Mano
            </span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className={`relative ${t.heroBg} overflow-hidden`} data-testid="landing-hero">
        {landing.hero_image_url && t.heroOverlay && (
          <div className="absolute inset-0">
            <img src={landing.hero_image_url} alt="" className="w-full h-full object-cover opacity-20" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {landing.show_timer && t.darkHero && <div className="mb-8"><DailyTimer theme={t} /></div>}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {landing.product_brand && (
                <span className={`inline-block text-xs font-bold uppercase tracking-wider ${t.heroBrand} mb-3`}>{landing.product_brand}</span>
              )}
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight ${t.heroText}`}
                style={{ fontFamily: "Outfit, sans-serif" }} data-testid="landing-title">
                {landing.title}
              </h1>
              {landing.subtitle && (
                <p className={`mt-4 text-base sm:text-lg ${t.heroSub} max-w-lg`}>{landing.subtitle}</p>
              )}

              {/* Price or Savings */}
              {landing.price_display === "savings" && landing.max_savings ? (
                <div className="mt-8" data-testid="landing-savings-badge">
                  <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-5 py-3">
                    <Sun className="w-6 h-6 text-emerald-500" />
                    <div>
                      <p className={`text-xs ${t.darkHero ? "text-emerald-300/80" : "text-emerald-600"} font-medium`}>Risparmio fino a</p>
                      <span className="text-3xl sm:text-4xl font-black text-emerald-500" style={{ fontFamily: "Outfit, sans-serif" }}>{landing.max_savings}</span>
                    </div>
                  </div>
                  {landing.price_note && <p className={`mt-2 text-xs ${t.priceNote}`}>{landing.price_note}</p>}
                </div>
              ) : landing.price_display !== "none" && landing.promo_price ? (
                <>
                  <div className="mt-8 flex items-end gap-3" data-testid="landing-price">
                    {landing.full_price && (
                      <span className={`text-xl ${t.darkHero ? "text-white/40" : "text-slate-400"} line-through`}>{landing.full_price}</span>
                    )}
                    <span className={`text-4xl sm:text-5xl font-black ${t.accent}`} style={{ fontFamily: "Outfit, sans-serif" }}>{landing.promo_price}</span>
                  </div>
                  {landing.price_note && <p className={`mt-2 text-xs ${t.priceNote}`}>{landing.price_note}</p>}
                </>
              ) : null}

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#form-section"
                  className={`inline-flex items-center gap-2 ${t.ctaBg} text-white font-bold rounded-full px-8 py-4 text-base shadow-xl btn-scale`}
                  data-testid="landing-hero-cta">
                  {landing.cta_text || "Richiedi Preventivo"} <ChevronRight className="w-5 h-5" />
                </a>
                {landing.brochure_url && (
                  <a href={landing.brochure_url} target="_blank" rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 font-semibold rounded-full px-6 py-4 text-sm border ${t.darkHero ? "border-white/20 text-white/80 hover:bg-white/10" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                    <Download className="w-4 h-4" /> Brochure PDF
                  </a>
                )}
              </div>

              {!t.darkHero && landing.show_timer && <div className="mt-8"><DailyTimer theme={t} /></div>}
            </div>

            {/* Product Image */}
            {(landing.product_image_url || landing.hero_image_url) && (
              <div className="hidden lg:flex items-center justify-center">
                <div className={`rounded-3xl overflow-hidden ${t.darkHero ? "shadow-2xl ring-1 ring-white/10" : "border border-slate-200 shadow-xl"} max-w-xl`}>
                  <img src={landing.product_image_url || landing.hero_image_url} alt={landing.title} className="w-full h-auto object-cover" loading="lazy" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      {landing.show_features !== false && features.length > 0 && (
        <section className="py-16 sm:py-20 bg-white" data-testid="landing-features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: "Outfit, sans-serif" }}>
              {landing.features_title || "Perche Scegliere Questo Prodotto"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const FIcon = featureIcons[i % featureIcons.length];
                return (
                  <div key={i} className={`rounded-xl border ${t.border} p-6 hover:shadow-lg transition-shadow`} data-testid={`landing-feature-${i}`}>
                    <div className={`w-10 h-10 rounded-lg ${t.featureIcon} flex items-center justify-center mb-4`}>
                      <FIcon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Specs */}
      {landing.show_specs !== false && specLines.length > 0 && (
        <section className={`py-16 sm:py-20 ${t.specBg}`} data-testid="landing-specs">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
              {landing.specs_title || "Specifiche Tecniche"}
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden shadow-sm">
              {specLines.map((line, i) => {
                const parts = line.split(":");
                const label = parts[0]?.trim();
                const value = parts.slice(1).join(":").trim();
                return (
                  <div key={i} className={`flex items-center text-sm ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                    <div className="font-semibold text-slate-700 px-6 py-4 w-2/5">{label}</div>
                    <div className="text-slate-500 px-6 py-4 flex-1">{value || line}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {landing.show_gallery !== false && landing.gallery_images?.length > 1 && (
        <section className="py-16 bg-white" data-testid="landing-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>{landing.gallery_title || "Galleria"}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {landing.gallery_images.map((img, i) => (
                <div key={i} className="rounded-xl overflow-hidden aspect-square bg-slate-50 border border-slate-100">
                  <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Calculator Section */}
      {landing.calculator_type && (
        <section className="py-12 sm:py-16 bg-white" data-testid="landing-calculator-section">
          <SavingsCalculator
            initialSector={landing.calculator_type}
            onConfigureClick={() => {
              const formEl = document.getElementById("form-section");
              if (formEl) formEl.scrollIntoView({ behavior: "smooth" });
            }}
          />
        </section>
      )}

      {/* Form Section */}
      <section id="form-section" className="py-16 sm:py-20 bg-slate-50" data-testid="landing-form-section">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              {landing.form_type === "calculator" ? "Calcola il Tuo Risparmio" : "Richiedi Informazioni"}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {landing.form_type === "calculator" ? "Rispondi a 3 domande e scopri quanto puoi risparmiare con il fotovoltaico." : "Compila il modulo e ti contatteremo senza impegno."}
            </p>
          </div>
          <div className={`bg-white rounded-2xl border ${t.border} p-6 sm:p-8 shadow-lg ring-4 ${t.ring}`}>
            {landing.form_type !== "calculator" && landing.price_display === "savings" && landing.max_savings ? (
              <div className="flex items-center justify-center gap-2 mb-6 pb-6 border-b border-slate-100">
                <Sun className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-500">Risparmio fino a</span>
                <span className={`text-2xl font-black text-emerald-500`} style={{ fontFamily: "Outfit, sans-serif" }}>{landing.max_savings}</span>
              </div>
            ) : landing.form_type !== "calculator" && landing.price_display !== "none" && landing.promo_price ? (
              <div className="flex items-center justify-center gap-3 mb-6 pb-6 border-b border-slate-100">
                {landing.full_price && <span className="text-lg text-slate-400 line-through">{landing.full_price}</span>}
                <span className={`text-3xl font-black ${t.accent}`} style={{ fontFamily: "Outfit, sans-serif" }}>{landing.promo_price}</span>
              </div>
            ) : null}
            {landing.form_type === "calculator"
              ? <CalculatorLeadForm landing={landing} theme={t} otpRequired={landing.otp_required} />
              : <LeadForm landing={landing} theme={t} otpRequired={landing.otp_required} />
            }
          </div>
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="flex items-center gap-1.5 text-xs text-slate-400"><Shield className="w-3.5 h-3.5" /> Senza impegno</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400"><Mail className="w-3.5 h-3.5" /> Risposta rapida</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400"><Phone className="w-3.5 h-3.5" /> Assistenza dedicata</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-slate-950 text-center">
        <a href="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Fotovoltaico Chiavi in Mano</a>
      </footer>
    </div>
  );
}
